import { Component, OnInit, Input, forwardRef, OnDestroy } from '@angular/core';
import { ApiService } from '../../../../../services/api.service';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_ASYNC_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import { Seller, SellerBranch } from '../../document-issue.model';
import { combineLatest, Observable, Subscription } from 'rxjs';


@Component({
  selector: 'app-seller-form',
  templateUrl: './seller-form.component.html',
  styleUrls: ['./seller-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SellerFormComponent),
      multi: true
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => SellerFormComponent),
      multi: true
    }
  ]
})
export class SellerFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  onChange: any = () => { };
  onTouched: any = () => { };

  @Input() isSubmitted: boolean;

  username = localStorage.getItem('username');

  sellerForm: FormGroup;
  subscription: Subscription;

  countries: any[];
  sellers: Seller[];

  isSellerHeadOffice = false;

  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService
  ) { }

  ngOnInit() {
    this.createSellerForm();

    // any time the inner form changes update the parent of any change
    this.createSubscriptions();

    this.getCountries();
    this.getSeller();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  writeValue(value: any) {
    if (value) {
      this.sellerForm.setValue(value);
      this.onChange(value);
      this.onTouched();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  // communicate the inner form validation to the parent form
  validate(_control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    return new Promise(resolve =>
      this.sellerForm.valid ? resolve(null) : resolve({ sellerForm: { valid: false } })
    );
  }

  createSubscriptions() {
    // Subscribe to valueChanges and statusChanges
    this.subscription = combineLatest([this.sellerForm.valueChanges, this.sellerForm.statusChanges]).subscribe(
      () => {
        const selectedSeller = this.sellers.find(seller => seller.name === this.sellerName.value);
        const selectedBranch = selectedSeller ? selectedSeller.branch.find(branch => branch.code === this.sellerBranchCode.value) : null;

        const engData = {
          nameEN: selectedSeller ? selectedSeller.name_en : null,
          addressEN: selectedBranch ? selectedBranch.address_en : null
        }

        this.onChange(Object.assign({}, this.sellerForm.getRawValue(), engData));
        this.onTouched();
      });
  }

  getSeller() {
    this.apiService.getCompany().subscribe(
        (res: any) => {
          this.sellers = res || [];

          if (this.sellers.length === 1) {
            this.setSeller(this.sellers[0]);
          }
        }
      );
  }

  getCountries() {
    this.apiService.getCountry().subscribe(
        (res: any) => {
          this.countries = res;
        }
      );
  }

  get sellerBranches() {
    const seller = this.sellers ? this.sellers.find(sellerItem => sellerItem.name === this.sellerName.value) : null;

    return seller ? seller.branch : [];
  }

  createSellerForm() {
    this.sellerForm = this.formBuilder.group({
      name: [null, Validators.required],
      taxID: [{ value: null, disabled: true }, Validators.required],
      branchCode: [{ value: null, disabled: true }],
      branchName: [{ value: null, disabled: true }],
      address: [{ value: null, disabled: true }, Validators.required],
      postalCode: [{ value: null, disabled: true }, Validators.required]
    });
  }

  get sellerName() {
    return this.sellerForm.get('name');
  }

  get sellerTaxID() {
    return this.sellerForm.get('taxID');
  }

  get sellerBranchCode() {
    return this.sellerForm.get('branchCode');
  }

  get sellerBranchName() {
    return this.sellerForm.get('branchName');
  }

  get sellerAddress() {
    return this.sellerForm.get('address');
  }

  get sellerPostalCode() {
    return this.sellerForm.get('postalCode');
  }

  setSeller(seller: Seller) {
    const name = seller ? seller.name : null;

    this.sellerName.setValue(name);

    this.setSellerBranch(null);

    if (seller.branch.length === 1) {
      this.sellerBranchCode.disable();
      this.setSellerBranch(seller.branch[0]);

    } else {
      this.sellerBranchCode.enable();
    }
  }

  setSellerBranch(branch: SellerBranch) {
    const taxID = this.sellers.find(seller => seller.name === this.sellerName.value).tax_id;
    const branchCode = branch ? branch.code : null;
    const branchName = branch ? branch.name : null;
    const address = branch ? branch.address : null;
    const postalCode = branch ? branch.post_code : null;
    const rdID = branch ? branch.rd_id : null;

    this.isSellerHeadOffice = branchCode === '00000' ? true : false;

    this.sellerForm.patchValue({
      taxID: rdID ? (taxID + rdID) : null,
      branchCode: branchCode,
      branchName: branchName,
      address: address,
      postalCode: postalCode
    });
  }

  setSellerHeadOffice() {
    if (this.isSellerHeadOffice) {
      const branch = this.sellerBranches.find(sellerBranche => sellerBranche.code === '00000');

      this.setSellerBranch(branch);
    } else {
      this.setSellerBranch(null);
    }
  }
}



// WEBPACK FOOTER //
// /opt/app/src/app/features/document/document-issue/components/seller-form/seller-form.component.ts